import React, { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Spinner, Dropdown } from "react-bootstrap";
import Copy from "../images/copy.png";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";
import { fetchAllCourses } from "../../../utils/courseUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";

interface Data {
  course_id: string;
  course_name: string;
  course_description: string;
  tracks: string[] | string; // Can be array or string
  track_names:string;
  course_level: string;
  batch_count?: number;
  days?: number;
  students_count: number;
  del_row: boolean;
  start_date?: string | null;
  end_date?: string | null;
}

interface Track {
  track_id: string;
  track_name: string;
}

const Courses: React.FC = () => {
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState<Data[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
    const [modalNewMessage, setModalNewMessage] = useState<string>("");
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<{
    course_id: string;
    course_name: string;
    course_description: string;
    course_level: string;
    tracks: string[];
    start_date: string;
    end_date: string;
  }>({
    course_id: "",
    course_name: "",
    course_description: "",
    course_level: "",
    tracks: [],
    start_date: "",
    end_date: "",
  });
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedCourse, setSelectedCourse] = useState<Data | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<string[]>([]);

  const toDateInput = (value?: string | null): string => {
    if (!value) return "";
    return String(value).slice(0, 10);
  };

  const levels = ["Level1", "Level2", "Level3"];
  const levelValues = ["level1", "level2", "level3"];

  const formatCourseLevel = (courseLevel: string): string => {
    if (!courseLevel || courseLevel.trim() === "") {
      return "--";
    }
    const selectedLevels = courseLevel.split(",").filter(level=>level.trim() !=="")
    const hasAllLevels = levelValues.every(level => selectedLevels.includes(level));
    if (hasAllLevels) {
      return "All";
    }

    const levelNumbers = selectedLevels
      .map(level => {
        const match = level.match(/level(\d+)/i);
        return match ? match[1] : null;
      })
      .filter(num => num !== null)
      .sort((a, b) => parseInt(a!) - parseInt(b!));
    
    return levelNumbers.join(",");
  };

  useEffect(() => {
    fetchCourses();
    fetchTracks();
  }, []);

  useEffect(() => {
  }, [tracks]);

  useEffect(() => {
  }, [selectedTracks]);


  const fetchCourses = async () => {
    try {
      const data = await fetchAllCourses();
      setCourses(data as Data[]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchTracks = async () => {
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  
  const handleModal = (type: string, course?: Data): void => {
    setModalType(type);
    if (type === "new") {
      setFormData({
        course_id: "",
        course_name: "",
        course_description: "",
        course_level: "",
        tracks: [],
        start_date: "",
        end_date: "",
      });
      setSelectAll(false);
      setSelectedTracks([]);
    } else if (course) {
      // For editing, we need to convert track_names to track IDs
      let trackIds: string[] = [];
      
      // Handle different data formats for tracks
      if (course.track_names && course.track_names.trim() !== "") {
        // Use track_names if available
        const trackNames = course.track_names.split(",").map(name => name.trim());
        trackIds = tracks
          .filter(track => trackNames.includes(track.track_name))
          .map(track => track.track_id);
      } else if (typeof course.tracks === 'string' && course.tracks.trim() !== "") {
        // Handle case where tracks is a string (like "Dummy track1" or "Dummy track1,z")
        const trackNames = course.tracks.split(",").map(name => name.trim());
        trackIds = tracks
          .filter(track => trackNames.includes(track.track_name))
          .map(track => track.track_id);
      } else if (Array.isArray(course.tracks) && course.tracks.length > 0) {
        // Handle case where tracks is an array of track IDs
        trackIds = course.tracks as string[];
      }
      
      setSelectedTracks(trackIds);
      setFormData({
        ...course,
        tracks: trackIds,
        start_date: toDateInput(course.start_date),
        end_date: toDateInput(course.end_date),
      });
      setSelectAll(
        levelValues.every((level) => course.course_level.includes(level))
      );
    }
    setShowModal(true);
    setError("");
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, checked } = e.target;

    if (value === "selectAll") {
      const newSelectAll = !selectAll;
      setSelectAll(newSelectAll);

      setFormData({
        ...formData,
        course_level: newSelectAll ? levelValues.join(",") : "",
      });
    } else {
      const updatedLevels = formData.course_level
        .split(",")
        .filter((level) => level !== "");

      if (checked) {
        updatedLevels.push(value);
      } else {
        const index = updatedLevels.indexOf(value);
        if (index > -1) {
          updatedLevels.splice(index, 1);
        }
      }

      const newLevel = updatedLevels.join(",");
      setFormData({
        ...formData,
        course_level: newLevel,
      });

      setSelectAll(updatedLevels.length === levelValues.length);
    }
  };

  const handleTrackChange = (trackId: string, checked: boolean): void => {
    let updatedTracks = [...selectedTracks];
    if (checked) {
      updatedTracks.push(trackId);
    } else {
      updatedTracks = updatedTracks.filter((track) => track !== trackId);
    }
    setSelectedTracks(updatedTracks);
    setFormData({
      ...formData,
      tracks: updatedTracks,
    });
  };
  

  const handleSave = async (): Promise<void> => {
    if (!formData.course_name || !formData.course_description) {
      setError("All fields are required");
      return;
    }
    if (formData.course_name.length > 50) {
      setError("Course name cannot exceed 50 characters");
      return;
    }
    if (formData.tracks.length === 0) {
      setError("Select atleast one track");
      return;
    }
    if (!formData.course_level || formData.course_level.trim() === "") {
      setError("Please select a level to save the course");
      return;
    }
    if ((formData.start_date && !formData.end_date) || (!formData.start_date && formData.end_date)) {
      setError("Set both the course start date and end date, or leave both empty");
      return;
    }
    if (formData.start_date && formData.end_date && formData.start_date >= formData.end_date) {
      setError("Course start date must be before the end date");
      return;
    }
    const isCourseTaken = courses.some(
      (course) =>
        course.course_name.toLowerCase() ===
          formData.course_name.toLowerCase() &&
        course.course_id?.toLowerCase() != formData.course_id?.toLowerCase()
    );
    if (isCourseTaken) {
      setError("Course name not available");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const json = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        by: sessionStorage.getItem("Email"),
      };
      const response = await authService.post("/admin/create_course/", json);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`Failed to ${modalType === "edit" ? "update" : "save"} Courses`);
      }

      fetchCourses();
      setShowModal(false);
      setLoading(false);
    } catch (error) {
      setError(`Error ${modalType === "edit" ? "updating" : "saving"} Courses`);
      console.error(`Error ${modalType === "edit" ? "updating" : "saving"} Courses:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (course: Data): void => {
    setSelectedCourse(course);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedCourse) {
      try {
        const json = {
          course_id: selectedCourse.course_id,
        };
        const response = await authService.post("admin/delete_course/", json);

        if (response.status !== 200 && response.status !== 201) {
          throw new Error("Failed to delete course");
        }
        fetchCourses();
        setShowDeleteModal(false);
      } catch (error) {
        console.error("Error deleting course:", error);
      }
    }
  };

  const handleCopy = (course: Data): void => {
    const textToCopy = `Course ID: ${course.course_id}\nCourse Name: ${course.course_name}\nCourse Description: ${course.course_description}\nCourse Level: ${course.course_level}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        // alert("Copied to clipboard!");
        setShowUpdateModal(true)
        setModalNewMessage("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}
    >
      <div className="d-flex justify-content-between pe-3">
        <span className="d-flex justify-content-start align-items-center ps-4">
          <label>Search</label>
          <Form.Control
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "150px" }}
            className="ms-2"
          />
        </span>
        <button
          className="btn"
          onClick={() => {
            handleModal("new");
          }}
        >
          + New Course
        </button>
      </div>
      <div className="p-4">
        <Table className="border-top mt-2">
          <thead
            style={{
              borderBottom: "1px solid black",
              borderTop: "1px solid black",
            }}
          >
            <tr>
              <td>Sl No</td>
              <td>Course Name</td>
              <td>Description</td>
              <td>Tracks</td>
              <td>Level</td>
              <td>Start</td>
              <td>End</td>
              <td>Students</td>
              <td>Batches</td>
              <td>Days</td>
              <td>Action</td>
            </tr>
          </thead>
          <tbody>
            {courses
              .filter(
                (course) =>
                  !course.del_row && 
                  course.course_name.toLowerCase().includes(search.toLowerCase())
              )
              .map((course, index) => (
                <tr key={index + 1}>
                  <td>{index + 1}</td>
                  <td>{course.course_name}</td>
                  <td>{course.course_description}</td>
                  <td>{course.track_names?course.track_names.replaceAll(",", ", "):"--"}</td> 
                  <td>{formatCourseLevel(course.course_level)}</td>
                  <td>{toDateInput(course.start_date) || "--"}</td>
                  <td>{toDateInput(course.end_date) || "--"}</td>
                  <td>{course.students_count}</td>
                  <td>{course.batch_count}</td>
                  <td>{course.days ? course.days : "--"}</td>
                  <td>
                    <FaEdit
                      className="fs-6 me-2"
                      role="button"
                      title="Edit"
                      onClick={() => {
                        handleModal("edit", course);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    <img
                      src={Copy}
                      alt="Copy"
                      role="button"
                      aria-label="Copy"
                      onClick={() => handleCopy(course)}
                      style={{ cursor: "pointer", marginRight: "8px" }}
                    />
                    <FaTrash
                      className="fs-6 me-2"
                      title="Delete"
                      role="button"
                      onClick={() => handleDelete(course)}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the course:{" "}
            <strong>{selectedCourse?.course_name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModal} width="80vw" onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "new" ? "Add New Course" : "Edit Course"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <p className="text-danger">{error}</p>}
          <Form>
            <Form.Group
              controlId="formTrack"
              className="d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2">Track</Form.Label>
              <Dropdown style={{ width: '50%' }}>
                <Dropdown.Toggle 
                  variant="outline-secondary" 
                  id="dropdown-basic" 
                  style={{ 
                    width: '100%', 
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {selectedTracks.length > 0 
                    ? `${selectedTracks.length} track(s) selected` 
                    : 'Select Tracks'
                  }
                </Dropdown.Toggle>
                <Dropdown.Menu 
                  style={{ 
                    width: '100%',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onSelect={(e) => e.preventDefault()}
                >
                 {tracks.map((track) => {
  const isChecked = selectedTracks.includes(track.track_id);
  const checkboxId = `track-checkbox-${track.track_id}`; // ✅ Unique ID

  return (
    <div 
      key={track.track_id} 
      style={{ padding: '8px 12px' }}
    >
      <Form.Check
        type="checkbox"
        id={checkboxId} // ✅ Assign unique id
        label={track.track_name}
        checked={isChecked}
        onChange={(e) => handleTrackChange(track.track_id, e.target.checked)}
      />
    </div>
  );
})}

                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <Form.Group
              controlId="formCourseName"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2">Course Name</Form.Label>
              <div style={{ width: "50%" }}>
                <Form.Control
                  type="text"
                  name="course_name"
                  value={formData.course_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 50) {
                      setFormData({ ...formData, course_name: value });
                      setError(""); // Clear error when valid
                    }
                  }}
                  isInvalid={formData.course_name.length > 50}
                  style={{
                    width: "100%",
                  }}
                />
                <div className="d-flex justify-content-between mt-1">
                  <small className={`text-${formData.course_name.length > 50 ? 'danger' : 'muted'}`}>
                    {formData.course_name.length}/50 characters
                  </small>
                  {formData.course_name.length > 50 && (
                    <small className="text-danger">Maximum 50 characters allowed</small>
                  )}
                </div>
              </div>
            </Form.Group>

            <Form.Group
              controlId="formCourseDescription"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label>Course Description</Form.Label>
              <Form.Control
                type="text"
                name="course_description"
                value={formData.course_description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    course_description: e.target.value,
                  })
                }
                style={{
                  width: "50%",
                }}
              />
            </Form.Group>

            <Form.Group
              controlId="formCourseStartDate"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label>Course Start Date</Form.Label>
              <Form.Control
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                style={{ width: "50%" }}
              />
            </Form.Group>

            <Form.Group
              controlId="formCourseEndDate"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label>Course End Date</Form.Label>
              <Form.Control
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData({ ...formData, end_date: e.target.value })
                }
                style={{ width: "50%" }}
              />
            </Form.Group>

            <Form.Group
              controlId="formCourseLevel"
              className="mt-3 d-flex  justify-content-start"
            >
              <Form.Label
                style={{
                  width: "50%",
                }}
              >
                Course Level
              </Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  value="selectAll"
                  label="Select All"
                  checked={selectAll}
                  onChange={handleLevelChange}
                />
                <div>
                  {levels.map((level, index) => (
                    <Form.Check
                      key={level}
                      type="checkbox"
                      value={levelValues[index]}
                      label={level}
                      checked={formData.course_level
                        .split(",")
                        .includes(levelValues[index])}
                      onChange={handleLevelChange}
                    />
                  ))}
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
          <Modal.Header closeButton>
              <Modal.Title>Message</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalNewMessage}</Modal.Body>
      </Modal>
    </div>
  );
};

export default Courses;
